import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, ModalVariant, toast } from '@/components';
import { SourceSelectionGrid } from '@/components/SourceSelectionGrid/SourceSelectionGrid';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { roundToTwoDecimals } from '@/utils/time';
import { SourceType, type FullSource } from '@/types/source';
import type { AddCustomEventModalFormValues } from './addCustomEventModalFormValues';
import { VideoTrimStep } from './VideoTrimStep';

interface AddCustomEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportId: string;
    onCreated: () => void;
}

type Step = 'source' | 'trim';

export function AddCustomEventModal({
    isOpen,
    onClose,
    reportId,
    onCreated,
}: AddCustomEventModalProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [step, setStep] = useState<Step>('source');
    const [selectedSource, setSelectedSource] = useState<FullSource | null>(
        null,
    );
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(0);

    const methods = useForm<AddCustomEventModalFormValues>({
        defaultValues: { promptText: '', description: '' },
    });

    const { handleSubmit, reset, watch, formState } = methods;
    const { isSubmitting } = formState;

    const promptTextValue = watch('promptText');

    useEffect(() => {
        if (isOpen) return;
        reset({ promptText: '', description: '' });
        setStep('source');
        setSelectedSource(null);
        setTrimStart(0);
        setTrimEnd(0);
    }, [isOpen, reset]);

    useEffect(() => {
        setScrollContainer(scrollContainerRef.current);
    }, [isOpen, step]);

    const handleSourceSelect = (source: FullSource) => {
        setSelectedSource(source);
    };

    const handleNext = () => {
        if (!selectedSource) return;
        setTrimStart(0);
        setTrimEnd(selectedSource.duration || 0);
        setStep('trim');
    };

    const handleBack = () => {
        setStep('source');
    };

    const onValidSubmit = async (values: AddCustomEventModalFormValues) => {
        if (!selectedSource) return;
        const trimmedName = values.promptText.trim();
        const trimmedDescription = values.description.trim();
        try {
            const videoStartSeconds = roundToTwoDecimals(trimStart);
            const videoEndSeconds = roundToTwoDecimals(trimEnd);
            await EngineService.createReportCustomEvent(reportId, {
                sourceId: selectedSource.id,
                promptText: trimmedName,
                description: trimmedDescription || undefined,
                videoStartSeconds,
                videoEndSeconds,
                thumbnailFrames: [
                    {
                        time: videoStartSeconds,
                        bbox: [
                            0,
                            0,
                            selectedSource.width,
                            selectedSource.height,
                        ],
                    },
                ],
            });
            toast.success(t('detail.addCustomEventModal.createSuccess'));
            onCreated();
            onClose();
        } catch {
            toast.error(t('detail.addCustomEventModal.createError'));
        }
    };

    const handleModalSubmit = () => {
        if (step === 'source') {
            handleNext();
            return;
        }
        void handleSubmit(onValidSubmit)();
    };

    const submitDisabled =
        step === 'source'
            ? !selectedSource
            : !promptTextValue?.trim() || trimEnd <= trimStart || isSubmitting;

    const submitText =
        step === 'source'
            ? t('detail.addCustomEventModal.next')
            : t('detail.addCustomEventModal.submit');

    return (
        <FormProvider {...methods}>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={t('detail.addCustomEventModal.title')}
                variant={ModalVariant.DEFAULT}
                onBack={step === 'trim' ? handleBack : undefined}
                onCancel={onClose}
                onSubmit={handleModalSubmit}
                submitText={submitText}
                submitDisabled={submitDisabled}
                submitInProgress={isSubmitting}
                size="medium"
                contentScrollUnderFooter
                scrollContainerRef={scrollContainerRef}
                closeOnOutsideClick
                contentTestId="add-custom-event-modal"
                submitButtonTestId="add-custom-event-submit"
            >
                {step === 'source' ? (
                    <SourceSelectionGrid
                        selectedSourceId={selectedSource?.id ?? null}
                        onSelect={handleSourceSelect}
                        scrollContainer={scrollContainer}
                        type={SourceType.FILE}
                        cacheScope="add-custom-event"
                        searchPlaceholder={t(
                            'detail.addCustomEventModal.searchPlaceholder',
                        )}
                        emptyLabel={t(
                            'detail.addCustomEventModal.noSourcesFound',
                        )}
                        loadingLabel={t(
                            'detail.addCustomEventModal.loadingSources',
                        )}
                    />
                ) : null}
                {step === 'trim' && selectedSource ? (
                    <VideoTrimStep
                        source={selectedSource}
                        trimStart={trimStart}
                        trimEnd={trimEnd}
                        onTrimChange={(start, end) => {
                            setTrimStart(start);
                            setTrimEnd(end);
                        }}
                    />
                ) : null}
            </Modal>
        </FormProvider>
    );
}
